import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'

import { useToastContext } from '../../../../../../../contexts'
import { PatchRequest } from '../../../../../../../data/models'
import { InvoiceLineType } from '../../../../../../../data/models/_generatedUblInvoice'
import { getHandleableError } from '../../../../../../../utils/errors/isXiaError'
import {
  requestUpdateEInvoiceLine,
  useAllEInvoiceLines,
} from '../../../../../data'
import { LinesForm } from './lines.form'

type Props = {
  editable?: boolean
}

export const LinesData: React.FC<Props> = ({ editable = false }) => {
  const { t } = useTranslation()
  const { showToast } = useToastContext()

  const { id: eInvoiceId } = useParams()
  const { data: invoiceLines, isLoading } = useAllEInvoiceLines(eInvoiceId)

  const [loadingRowIds, setLoadingRowIds] = useState<Set<string>>(new Set())

  // Functions for handling the loading states of the buttons
  const addLoadingRowIds = (rowIds: string[]) => {
    setLoadingRowIds(prevState => {
      const updatedSet = new Set(prevState)
      rowIds.forEach(rowId => updatedSet.add(rowId))
      return updatedSet
    })
  }

  const removeLoadingRowIds = (rowIds: string[]) => {
    setLoadingRowIds(prevState => {
      const updatedSet = new Set(prevState)
      rowIds.forEach(rowId => updatedSet.delete(rowId))
      return updatedSet
    })
  }

  const hasLoadingRowId = (rowId: string) => {
    return loadingRowIds.has(rowId)
  }

  const patchFromFormValues = (
    formData: InvoiceLineType,
  ): PatchRequest<InvoiceLineType> => {
    const patchOperations: PatchRequest<InvoiceLineType> = []

    const originalInvoiceLine = invoiceLines?.find(
      line => line.LineId === formData.LineId,
    )

    // LineID - Handle IsEditEnabled checkbox changes
    if (
      formData?.OrderLineReference?.LineID?.IsEditEnabled !==
      originalInvoiceLine?.OrderLineReference?.LineID?.IsEditEnabled
    ) {
      // Save checkbox state
      patchOperations.push({
        op: originalInvoiceLine?.OrderLineReference?.LineID?.IsEditEnabled
          ? 'replace'
          : 'add',
        path: '/OrderLineReference/LineID/IsEditEnabled',
        value: formData?.OrderLineReference?.LineID?.IsEditEnabled!,
      })

      // If checkbox is now unchecked, reset to original value
      if (!formData?.OrderLineReference?.LineID?.IsEditEnabled) {
        patchOperations.push({
          op: originalInvoiceLine?.OrderLineReference?.LineID?.Value
            ? 'replace'
            : 'add',
          path: '/OrderLineReference/LineID/Value',
          value: formData?.OrderLineReference?.LineID?.OriginalValue || '',
        })
      }
    }

    // LineID - Handle value changes (only if checkbox is enabled)
    if (
      formData?.OrderLineReference?.LineID?.IsEditEnabled &&
      formData?.OrderLineReference?.LineID?.Value !==
        originalInvoiceLine?.OrderLineReference?.LineID?.Value
    ) {
      patchOperations.push({
        op: originalInvoiceLine?.OrderLineReference?.LineID?.Value
          ? 'replace'
          : 'add',
        path: '/OrderLineReference/LineID/Value',
        value: formData?.OrderLineReference?.LineID?.Value || '',
      })
    }

    // BuyersItemID - Handle IsEditEnabled checkbox changes
    if (
      formData?.Item?.BuyersItemIdentification?.ID?.Value?.IsEditEnabled !==
      originalInvoiceLine?.Item?.BuyersItemIdentification?.ID?.Value
        ?.IsEditEnabled
    ) {
      // Save checkbox state
      patchOperations.push({
        op: originalInvoiceLine?.Item?.BuyersItemIdentification?.ID?.Value
          ?.IsEditEnabled
          ? 'replace'
          : 'add',
        path: '/Item/BuyersItemIdentification/ID/Value/IsEditEnabled',
        value:
          formData?.Item?.BuyersItemIdentification?.ID?.Value?.IsEditEnabled!,
      })

      // If checkbox is now unchecked, reset to original value
      if (!formData?.Item?.BuyersItemIdentification?.ID?.Value?.IsEditEnabled) {
        patchOperations.push({
          op: originalInvoiceLine?.Item?.BuyersItemIdentification?.ID?.Value
            ?.Value
            ? 'replace'
            : 'add',
          path: '/Item/BuyersItemIdentification/ID/Value/Value',
          value:
            formData?.Item?.BuyersItemIdentification?.ID?.Value
              ?.OriginalValue || '',
        })
      }
    }

    // BuyersItemID - Handle value changes (only if checkbox is enabled)
    if (
      formData?.Item?.BuyersItemIdentification?.ID?.Value?.IsEditEnabled &&
      formData?.Item?.BuyersItemIdentification?.ID?.Value?.Value !==
        originalInvoiceLine?.Item?.BuyersItemIdentification?.ID?.Value?.Value
    ) {
      patchOperations.push({
        op: originalInvoiceLine?.Item?.BuyersItemIdentification?.ID?.Value
          ?.Value
          ? 'replace'
          : 'add',
        path: '/Item/BuyersItemIdentification/ID/Value/Value',
        value: formData?.Item?.BuyersItemIdentification?.ID?.Value?.Value || '',
      })
    }

    return patchOperations
  }

  const handleSave = async (invoiceLine: InvoiceLineType) => {
    try {
      addLoadingRowIds([invoiceLine.LineId!])

      const patchData: PatchRequest<InvoiceLineType> =
        patchFromFormValues(invoiceLine)

      if (patchData.length > 0) {
        await requestUpdateEInvoiceLine(
          eInvoiceId!,
          invoiceLine.LineId!,
          patchData,
        )

        showToast({
          severity: 'success',
          summary: t(
            'eInvoiceDetails.stepper.additionalData.positions.table.save.messages.success.summary',
          ),
          detail: t(
            'eInvoiceDetails.stepper.additionalData.positions.table.save.messages.success.detail',
            {
              lineId: invoiceLine?.OrderLineReference?.LineID?.Value,
            },
          ),
        })
      }
    } catch (error) {
      const handleableError = getHandleableError(error)
      const errorMessage = handleableError.message

      showToast({
        severity: 'error',
        summary: t(
          'eInvoiceDetails.stepper.additionalData.positions.table.save.messages.failure.summary',
        ),
        detail: errorMessage,
        sticky: true,
      })
    } finally {
      removeLoadingRowIds([invoiceLine.LineId!])
    }
  }

  return (
    <LinesForm
      invoiceLines={invoiceLines}
      isLoading={isLoading}
      hasLoadingRowId={hasLoadingRowId}
      handleSave={handleSave}
      editable={editable}
    />
  )
}
